import { APP_POOL_REGION_PATTERN } from "./region-grammar.js";
import { APP_POOL_NODE_GROUP_ID_PATTERN, APP_POOL_NODE_TYPE_PATTERN, APP_POOL_WORKLOADS, appPoolNameSchema } from "./app-pool.js";
import { APP_POOL_PLACEMENT_STATES } from "./app-pool-submit.js";
import { AppPoolTiersStatusSchema } from "./app-pool-tier-status.js";
import { z } from "zod";
//#region src/app-pool-admin.ts
/**
* The operator's read of a data plane's app pools (2026-09-04-app-pools, R-013): what the operator
* reported per pool (`AppPoolReportedStatus` fields) joined with the CP's refusal record. Served by
* site-admin-api (WS-3, R-015), guarded by `cp:dp:read` like every sibling read in
* `dp-admin.controller.ts`, rendered on the console's data-plane capacity tab as a pool table.
*/
const APP_POOL_ADMIN_PATH = "/v1/admin/data-planes/:dpId/app-pools";
/** The scope the route requires. `cp:admin` does not exist in this tree; this one does. */
const APP_POOL_ADMIN_SCOPE = "cp:dp:read";
/** One pool as the operator sees it. `unpriced` is true when the CP refused the pool's node group
*  for want of a metering rate (R-010). `members` is the standing member count in force, 0 for a
*  per-attempt pool. */
const appPoolAdminRowSchema = z.object({
	appKey: z.string().min(1).max(63),
	pool: appPoolNameSchema,
	workload: z.enum(APP_POOL_WORKLOADS),
	placement: z.enum(APP_POOL_PLACEMENT_STATES),
	unschedulableReason: z.string().min(1).max(512).nullable(),
	groupId: z.string().regex(APP_POOL_NODE_GROUP_ID_PATTERN).nullable(),
	nodeType: z.string().regex(APP_POOL_NODE_TYPE_PATTERN).max(40).nullable(),
	/** The pool's declared `region`, `null` for a home pool (R-045). The console shows it beside `nodeType`. */
	region: z.string().regex(APP_POOL_REGION_PATTERN).nullable(),
	depth: z.number().int().min(0),
	inFlight: z.number().int().min(0),
	members: z.number().int().min(0),
	concurrency: z.number().int().min(0),
	warmFloor: z.number().int().min(0),
	maxNodes: z.number().int().min(0),
	unpriced: z.boolean(),
	/** The status of the pool's `PoolTiers` object (2026-10-24-pool-machine-generations R-019), from
	*  the reported row's `poolTiers`. The CP writes `null` when the report omits it: a pool with no
	*  `tiers`, or an operator that does not report it. Required and nullable, the `region` posture. */
	poolTiers: AppPoolTiersStatusSchema.nullable()
}).strict();
/** `rows` is `null` when the operator has never reported `appPools` for this data plane
*  (`dp_live_status.app_pools IS NULL`) and `[]` when it reported none. The two are kept apart
*  the way `livePools` and `unsatisfiedDemand` keep them apart in `dp-live-status.ts`; the console
*  renders `NotCollectedState` for the first and `EmptyState` for the second (R-013, WS-3). */
const appPoolAdminResponseSchema = z.object({
	dpId: z.string().min(1),
	measuredAt: z.string().datetime(),
	rows: z.array(appPoolAdminRowSchema).nullable()
}).strict();
//#endregion
export { APP_POOL_ADMIN_PATH, APP_POOL_ADMIN_SCOPE, appPoolAdminResponseSchema, appPoolAdminRowSchema };

//# sourceMappingURL=app-pool-admin.js.map